import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_URL } from '../config';
import { useAuth } from './AuthContext';
import { getUserId, getToken } from '../auth';

interface SocketContextType {
  orderUpdateCount: number;
  credentialUpdateCount: number;
  happyHourUpdateCount: number;
}

const SocketContext = createContext<SocketContextType>({
  orderUpdateCount: 0,
  credentialUpdateCount: 0,
  happyHourUpdateCount: 0,
});

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, handleBanned } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [orderUpdateCount, setOrderUpdateCount] = useState(0);
  const [credentialUpdateCount, setCredentialUpdateCount] = useState(0);
  const [happyHourUpdateCount, setHappyHourUpdateCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    // Round-9 / C1 phase 2 — pass the access token at connect so the
    // server can place this socket in `user:{id}` + role-keyed rooms.
    // Phase 3 (server flip) sends room-targeted emits, which means a
    // socket without auth no longer receives any events.
    let cancelled = false;
    let cleanupSocket: Socket | null = null;
    void (async () => {
      const token = await getToken();
      if (cancelled) return;
      const socket = io(API_URL, {
        transports: ['websocket'],
        reconnectionDelay: 3000,
        auth: token ? { token } : undefined,
      });
      socketRef.current = socket;
      cleanupSocket = socket;

      socket.on('orderStatusUpdated', () => {
        setOrderUpdateCount(n => n + 1);
      });

      socket.on('credentialUpdate', () => {
        setCredentialUpdateCount(n => n + 1);
      });

      socket.on('happyHourUpdated', () => {
        setHappyHourUpdateCount(n => n + 1);
      });

      socket.on('userStatusChanged', async (payload: { userId: string; status: string }) => {
        if (!payload || (payload.status !== 'banned' && payload.status !== 'suspended')) return;
        const myId = await getUserId();
        if (myId && payload.userId === myId) {
          handleBanned();
        }
      });
    })();

    return () => {
      cancelled = true;
      cleanupSocket?.disconnect();
      socketRef.current = null;
    };
  }, [isLoggedIn, handleBanned]);

  return (
    <SocketContext.Provider value={{ orderUpdateCount, credentialUpdateCount, happyHourUpdateCount }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
